import React, { useState, useEffect } from 'react';
import { UserCircle, Save, Eye, EyeOff, Lock, User, Mail, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { api_url_v1 } from '../utils/Config';
import { useSelector } from 'react-redux';

function Profile() {
//   const { adminId } = useParams();
  const {token,id} = useSelector((state)=>state.auth)
  
  // Profile state
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  
  // Password state
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // UI states
  const [isLoading, setIsLoading] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [notification, setNotification] = useState({
    profile: { show: false, type: '', message: '' },
    password: { show: false, type: '', message: '' }
  });
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      setIsProfileLoading(true);
      try {
        // Get the admin ID from URL params or from local storage/auth context
        
        const response = await axios.get(`${api_url_v1}/${id}/show`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data && response.data.data) {
          setProfile({
            firstName: response.data.data.first_name || '',
            lastName: response.data.data.last_name || '',
            email: response.data.data.email || ''
          });
        }
        setError(null);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setError('Failed to load profile data. Please try again later.');
        showNotification('profile', 'error', 'Failed to load profile data');
      } finally {
        setIsProfileLoading(false);
      }
    };
    
    fetchProfile();
  }, [id]);

  // Handle profile form changes
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  // Handle password form changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords(prev => ({ ...prev, [name]: value }));
  };

  // Display notification
  const showNotification = (form, type, message) => {
    setNotification(prev => ({
      ...prev,
      [form]: { show: true, type, message }
    }));
    
    // Auto hide after 5 seconds
    setTimeout(() => {
      setNotification(prev => ({
        ...prev,
        [form]: { ...prev[form], show: false }
      }));
    }, 5000);
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Update profile
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Validate input
      if (!profile.firstName || !profile.lastName || !profile.email) {
        showNotification('profile', 'error', 'All fields are required');
        setIsLoading(false);
        return;
      }
      
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(profile.email)) {
        showNotification('profile', 'error', 'Please enter a valid email address');
        setIsLoading(false);
        return;
      }
      
      // Prepare data for API
      const profileData = {
        first_name: profile.firstName,
        last_name: profile.lastName,
        email: profile.email
      };
      
      // Make API request
      const response = await axios.put(`${api_url_v1}/update-profile`, profileData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.status === 200) {
        showNotification('profile', 'success', 'Profile updated successfully');
        setIsEditing(false);
      } else {
        showNotification('profile', 'error', response.data.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      showNotification(
        'profile', 
        'error', 
        error.response?.data?.message || 'Failed to update profile'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Update password
  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Validate input
      if (!passwords.currentPassword || !passwords.newPassword || !passwords.confirmPassword) {
        showNotification('password', 'error', 'All password fields are required');
        setIsLoading(false);
        return;
      }
      
      // Password match validation
      if (passwords.newPassword !== passwords.confirmPassword) {
        showNotification('password', 'error', 'New passwords do not match');
        setIsLoading(false);
        return;
      }
      
      // Password strength validation
      if (passwords.newPassword.length < 8) {
        showNotification('password', 'error', 'Password must be at least 8 characters long');
        setIsLoading(false);
        return;
      }
      
      // Make API request
      const response = await axios.put(`${api_url_v1}/update-password-profile`, {
        old_password: passwords.currentPassword,
        password: passwords.newPassword,
        password_confirmation: passwords.confirmPassword
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.status == 200) {
        showNotification('password', 'success', 'Password updated successfully');
        setPasswords({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        showNotification('password', 'error', response.data.message || 'Failed to update password');
      }
    } catch (error) {
      console.error('Error updating password:', error);
      showNotification(
        'password', 
        'error', 
        error.response?.data?.message || 
        (error.response?.status === 422 ? 'Current password is incorrect' : 'Failed to update password')
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while fetching profile
  if (isProfileLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-t-4 border-b-4 border-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Show error state if profile fetch failed
  if (error && !profile.email) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Profile</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Admin Profile</h1>
          <p className="text-gray-600">Manage your personal information and security settings</p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Avatar/Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <div className="relative inline-block">
                <div className="w-32 h-32 rounded-full bg-blue-100 flex items-center justify-center mb-4 mx-auto">
                  <UserCircle size={80} className="text-blue-600" />
                </div>
              </div>
              <h2 className="text-xl font-semibold mb-1">{profile.firstName} {profile.lastName}</h2>
              <p className="text-gray-600 mb-4">{profile.email}</p>
              <p className="text-sm text-gray-500">Administrator</p>
            </div>
          </div>
          
          {/* Profile Forms */}
          <div className="lg:col-span-2 space-y-8">
            {/* Profile Information Form */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">Profile Information</h2>
                  <button 
                    onClick={() => setIsEditing(!isEditing)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    {isEditing ? 'Cancel' : 'Edit'}
                  </button>
                </div>
                
                {notification.profile.show && (
                  <div className={`mb-4 p-3 rounded-md flex items-center ${
                    notification.profile.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}>
                    {notification.profile.type === 'success' ? 
                      <CheckCircle size={20} className="mr-2" /> : 
                      <AlertCircle size={20} className="mr-2" />}
                    <span>{notification.profile.message}</span>
                  </div>
                )}
                
                <form onSubmit={handleUpdateProfile}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <User size={18} className="text-gray-400" />
                        </div>
                        <input
                          type="text"
                          name="firstName"
                          value={profile.firstName}
                          onChange={handleProfileChange}
                          disabled={!isEditing}
                          className={`w-full pl-10 pr-3 py-2 border ${isEditing ? 'bg-white' : 'bg-gray-50'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <User size={18} className="text-gray-400" />
                        </div>
                        <input
                          type="text"
                          name="lastName"
                          value={profile.lastName}
                          onChange={handleProfileChange}
                          disabled={!isEditing}
                          className={`w-full pl-10 pr-3 py-2 border ${isEditing ? 'bg-white' : 'bg-gray-50'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail size={18} className="text-gray-400" />
                      </div>
                      <input
                        type="email"
                        name="email"
                        value={profile.email}
                        onChange={handleProfileChange}
                        disabled={!isEditing}
                        className={`w-full pl-10 pr-3 py-2 border ${isEditing ? 'bg-white' : 'bg-gray-50'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                    </div>
                  </div>
                  
                  {isEditing && (
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        {isLoading ? (
                          <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
                        ) : (
                          <Save size={18} className="mr-2" />
                        )}
                        Save Changes
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>
            
            {/* Password Update Form */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-6">Update Password</h2>
                
                {notification.password.show && (
                  <div className={`mb-4 p-3 rounded-md flex items-center ${
                    notification.password.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}>
                    {notification.password.type === 'success' ? 
                      <CheckCircle size={20} className="mr-2" /> : 
                      <AlertCircle size={20} className="mr-2" />}
                    <span>{notification.password.message}</span>
                  </div>
                )}
                
                <form onSubmit={handleUpdatePassword}>
                  <div className="space-y-4 mb-6">
                    {/* Current Password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock size={18} className="text-gray-400" />
                        </div>
                        <input
                          type={showPassword.current ? "text" : "password"}
                          name="currentPassword"
                          value={passwords.currentPassword}
                          onChange={handlePasswordChange}
                          className="w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility('current')}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                        >
                          {showPassword.current ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                    
                    {/* New Password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock size={18} className="text-gray-400" />
                        </div>
                        <input
                          type={showPassword.new ? "text" : "password"}
                          name="newPassword"
                          value={passwords.newPassword}
                          onChange={handlePasswordChange}
                          className="w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility('new')}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                        >
                          {showPassword.new ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      <p className="mt-1 text-sm text-gray-500">Must be at least 8 characters</p>
                    </div>
                    
                    {/* Confirm New Password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock size={18} className="text-gray-400" />
                        </div>
                        <input
                          type={showPassword.confirm ? "text" : "password"}
                          name="confirmPassword"
                          value={passwords.confirmPassword}
                          onChange={handlePasswordChange}
                          className="w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility('confirm')}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                        >
                          {showPassword.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      {isLoading ? (
                        <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
                      ) : (
                        <Lock size={18} className="mr-2" />
                      )}
                      Update Password
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;